import { User } from '@sentry/react';
import { isEmpty } from 'lodash/fp';
import { DateTime } from 'luxon';

import NavBack from 'dashboard/components/NavBack';
import Pii from 'dashboard/components/Pii';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import {
  Backdrop,
  CardContainer,
  Container,
  HeaderContainer,
} from 'dashboard/layouts/ShowViewLayoutV2';
import { UserAuditLog } from 'dashboard/models/user-audit-log';
import Card from 'design-system/components/Card';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';

interface DashboardUserAuditLogsShowViewProps {
  userAuditLog: UserAuditLog | undefined;
  user: User | undefined;
  loading: boolean;
}

const TAB_WIDTH = 2;

const DashboardUserAuditLogsShowView = ({
  userAuditLog,
  user,
  loading,
}: DashboardUserAuditLogsShowViewProps) => {
  if (loading) {
    return <LoadingIndicator fullscreen={true} />;
  }

  if (userAuditLog == null) {
    return (
      <Backdrop>
        <NullState
          message='We couldn’t find that API log.'
          backRoute='/dashboard/user-audit-logs'
        />
      </Backdrop>
    );
  }

  const {
    method,
    path,
    'ip-address': ipAddress,
    'user-agent': userAgent,
    'get-params': getParams,
    'post-params': postParams,
    context,
    'created-at': createdAt,
  } = userAuditLog.attributes;

  const title = `${method} ${path}`;

  return (
    <Backdrop>
      <Container>
        <HeaderContainer>
          <NavBack to='/dashboard/user-audit-logs' dataTest='user-audit-log-show__nav-back'>
            {title}
          </NavBack>
        </HeaderContainer>

        <CardContainer>
          <Card>
            <Card.Body>
              <Card.Subtitle>User</Card.Subtitle>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              <Card.Title>{user.attributes['email-address']}</Card.Title>
            </Card.Body>
          </Card>
          <Card>
            <Card.Body>
              <Card.Subtitle>Accessed at</Card.Subtitle>
              <Card.Title>{DateTime.fromISO(createdAt).toFormat(DATE_AND_TIME_FORMAT)}</Card.Title>
            </Card.Body>
          </Card>
          <Card>
            <Card.Body>
              <Card.Subtitle>IP Address</Card.Subtitle>
              <Card.Title>{ipAddress}</Card.Title>
            </Card.Body>
          </Card>
        </CardContainer>

        <Card>
          <Card.Body>
            <Card.Title>User agent</Card.Title>
            <p>{userAgent}</p>

            {!isEmpty(getParams) && (
              <div>
                <Card.Title>Query params</Card.Title>
                <Pii isRedacted={false}>
                  <CodeHighlight
                    id='user-audit-log-show__code--params'
                    language='json'
                    rowsToShow={20}
                    light={true}
                  >
                    {JSON.stringify(getParams, null, TAB_WIDTH)}
                  </CodeHighlight>
                </Pii>
              </div>
            )}
            {!isEmpty(postParams) && (
              <div>
                <Card.Title>POST params</Card.Title>
                <Pii isRedacted={false}>
                  <CodeHighlight
                    id='user-audit-log-show__code--params'
                    language='json'
                    rowsToShow={20}
                  >
                    {JSON.stringify(postParams, null, TAB_WIDTH)}
                  </CodeHighlight>
                </Pii>
              </div>
            )}
            {isEmpty(getParams) && isEmpty(postParams) && (
              <div>
                <p>This request did not have any parameters.</p>
              </div>
            )}

            {!isEmpty(context) && (
              <div>
                <Card.Title>Context</Card.Title>
                <Pii isRedacted={false}>
                  <CodeHighlight
                    id='user-audit-log-show__code--context'
                    language='json'
                    rowsToShow={20}
                  >
                    {JSON.stringify(context, null, TAB_WIDTH)}
                  </CodeHighlight>
                </Pii>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </Backdrop>
  );
};

export default DashboardUserAuditLogsShowView;
